import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { UserBalance, Transaction, CreditCard, ApiResponse } from '@/types';
import api from '@/services/authService';

interface UserState {
  balances: UserBalance[];
  transactions: Transaction[];
  creditCards: CreditCard[];
  isLoading: boolean;
  error: string | null;
}

const initialState: UserState = {
  balances: [],
  transactions: [],
  creditCards: [],
  isLoading: false,
  error: null,
};

// Async thunks
export const getBalances = createAsyncThunk(
  'user/getBalances',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Fetching balances...');
      const response = await api.get('/user/balances');
      console.log('Balances response:', response.data);
      if (response.data.success) {
        console.log('Balances data:', response.data.data);
        // Backend returns balances directly in data.data, not data.data.balances
        return Array.isArray(response.data.data) ? response.data.data : [];
      }
      throw new Error(response.data.error?.message || 'Failed to fetch balances');
    } catch (error: any) {
      console.error('Error fetching balances:', error);
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch balances');
    }
  }
);

export const getTransactions = createAsyncThunk(
  'user/getTransactions',
  async (params: { page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      console.log('Fetching transactions...', params);
      const response = await api.get('/user/transactions', { params });
      console.log('Transactions response:', response.data);
      if (response.data.success) {
        console.log('Transactions data:', response.data.data);
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to fetch transactions');
    } catch (error: any) {
      console.error('Error fetching transactions:', error);
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch transactions');
    }
  }
);

export const getCreditCards = createAsyncThunk(
  'user/getCreditCards',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Fetching credit cards...');
      const response = await api.get('/user/credit-cards');
      console.log('Credit cards response:', response.data);
      if (response.data.success) {
        console.log('Credit cards data:', response.data.data);
        return response.data.data.creditCards;
      }
      throw new Error(response.data.error?.message || 'Failed to fetch credit cards');
    } catch (error: any) {
      console.error('Error fetching credit cards:', error);
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch credit cards');
    }
  }
);

export const addCreditCard = createAsyncThunk(
  'user/addCreditCard',
  async (cardData: any, { rejectWithValue }) => {
    try {
      const response = await api.post('/user/credit-cards', cardData);
      if (response.data.success) {
        return response.data.data.creditCard;
      }
      // Include validation details if available
      const errorMessage = response.data.error?.details
        ? `${response.data.error.message}: ${response.data.error.details.map((d: any) => d.msg).join(', ')}`
        : response.data.error?.message || 'Failed to add credit card';
      throw new Error(errorMessage);
    } catch (error: any) {
      const errorResponse = error.response?.data?.error;
      const errorMessage = errorResponse?.details
        ? `${errorResponse.message}: ${errorResponse.details.map((d: any) => d.msg).join(', ')}`
        : errorResponse?.message || error.message || 'Failed to add credit card';
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateCreditCard = createAsyncThunk(
  'user/updateCreditCard',
  async ({ id, ...updateData }: any, { rejectWithValue }) => {
    try {
      const response = await api.put(`/user/credit-cards/${id}`, updateData);
      if (response.data.success) {
        return response.data.data.creditCard;
      }
      throw new Error(response.data.error?.message || 'Failed to update credit card');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update credit card');
    }
  }
);

export const deleteCreditCard = createAsyncThunk(
  'user/deleteCreditCard',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/user/credit-cards/${id}`);
      if (response.data.success) {
        return id;
      }
      throw new Error(response.data.error?.message || 'Failed to delete credit card');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to delete credit card');
    }
  }
);

export const withdraw = createAsyncThunk(
  'user/withdraw',
  async (withdrawData: any, { rejectWithValue }) => {
    try {
      const response = await api.post('/user/withdraw', withdrawData);
      if (response.data.success) {
        return response.data.data.transaction;
      }
      throw new Error(response.data.error?.message || 'Failed to process withdrawal');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to process withdrawal');
    }
  }
);

export const deposit = createAsyncThunk(
  'user/deposit',
  async (depositData: { amount: number; currency: 'EUR' | 'USD'; method: string; cardId?: number; description?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/user/deposit', depositData);
      if (response.data.success) {
        return response.data.data.transaction;
      }
      throw new Error(response.data.error?.message || 'Failed to process deposit');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to process deposit');
    }
  }
);

export const transfer = createAsyncThunk(
  'user/transfer',
  async (transferData: { recipientUsername: string; amount: number; currency: 'EUR' | 'USD'; description?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/user/transfer', transferData);
      if (response.data.success) {
        return response.data.data.transaction;
      }
      throw new Error(response.data.error?.message || 'Failed to process transfer');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to process transfer');
    }
  }
);

export const transferRequest = createAsyncThunk(
  'user/transferRequest',
  async (transferData: { paymentMethod: 'account' | 'card'; amount: number; currency: 'EUR' | 'USD'; details: any }, { rejectWithValue }) => {
    try {
      const response = await api.post('/user/transfer-request', transferData);
      if (response.data.success) {
        return response.data.data.transaction;
      }
      throw new Error(response.data.error?.message || 'Failed to process transfer request');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to process transfer request');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'user/updateProfile',
  async (profileData: any, { rejectWithValue }) => {
    try {
      const response = await api.put('/user/profile', profileData);
      if (response.data.success) {
        return response.data.data.user;
      }
      throw new Error(response.data.error?.message || 'Failed to update profile');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update profile');
    }
  }
);

// Slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearUserState: (state) => {
      state.balances = [];
      state.transactions = [];
      state.creditCards = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Get Balances
    builder
      .addCase(getBalances.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBalances.fulfilled, (state, action) => {
        state.isLoading = false;
        state.balances = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;
      })
      .addCase(getBalances.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Get Transactions
    builder
      .addCase(getTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        // Transform snake_case to camelCase for transaction fields
        const transactions = action.payload.transactions || [];
        state.transactions = transactions.map((tx: any) => ({
          id: tx.id,
          userId: tx.user_id,
          transactionType: tx.transaction_type,
          amount: tx.amount,
          currency: tx.currency,
          status: tx.status,
          description: tx.description,
          recipientUsername: tx.recipient_username,
          createdAt: tx.created_at,
          completedAt: tx.completed_at,
          updatedAt: tx.updated_at,
          referenceId: tx.reference_id,
          creditCardId: tx.credit_card_id,
          creditCard: tx.creditCard,
        }));
        state.error = null;
      })
      .addCase(getTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Get Credit Cards
    builder
      .addCase(getCreditCards.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCreditCards.fulfilled, (state, action) => {
        state.isLoading = false;
        state.creditCards = action.payload;
        state.error = null;
      })
      .addCase(getCreditCards.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Add Credit Card
    builder
      .addCase(addCreditCard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addCreditCard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.creditCards.push(action.payload);
        state.error = null;
      })
      .addCase(addCreditCard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Credit Card
    builder
      .addCase(updateCreditCard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCreditCard.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.creditCards.findIndex(card => card.id === action.payload.id);
        if (index !== -1) {
          state.creditCards[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateCreditCard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Delete Credit Card
    builder
      .addCase(deleteCreditCard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCreditCard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.creditCards = state.creditCards.filter(card => card.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteCreditCard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Withdraw
    builder
      .addCase(withdraw.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(withdraw.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transactions.unshift(action.payload);
        state.error = null;
      })
      .addCase(withdraw.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Deposit
    builder
      .addCase(deposit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deposit.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transactions.unshift(action.payload);
        state.error = null;
      })
      .addCase(deposit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Transfer
    builder
      .addCase(transfer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(transfer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transactions.unshift(action.payload);
        state.error = null;
      })
      .addCase(transfer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Transfer Request
    builder
      .addCase(transferRequest.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(transferRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transactions.unshift(action.payload);
        state.error = null;
      })
      .addCase(transferRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Profile
    builder
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // Return the updated user so auth slice can update
        return action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearUserState } = userSlice.actions;
export default userSlice.reducer;
